/**
 * Summarize bulk config-push API results (connectivity, treemgmt patch, …).
 * Success without command_uuid means config was saved but the device was
 * offline — push is pending until reconnect.
 */

export function mergeConfigPushResultsBySerial(results) {
  if (!Array.isArray(results)) return [];
  const bySerial = new Map();

  results.forEach((result) => {
    const serial = String(result?.serial || '').toLowerCase();
    if (!serial) return;

    const prev = bySerial.get(serial);
    if (!prev) {
      bySerial.set(serial, { ...result, serial });
      return;
    }

    if (!result.success || !prev.success) {
      bySerial.set(serial, {
        ...prev,
        success: false,
        message: prev.success ? result.message : prev.message,
      });
      return;
    }

    if (result.command_uuid || prev.command_uuid) {
      bySerial.set(serial, {
        ...prev,
        command_uuid: prev.command_uuid || result.command_uuid,
        command_id: prev.command_id || result.command_id,
        message: prev.command_uuid ? prev.message : result.message,
      });
    }
  });

  return Array.from(bySerial.values());
}

export function classifyConfigPushResults(results) {
  const merged = mergeConfigPushResultsBySerial(results);
  const applied = merged.filter((r) => r.success && r.command_uuid);
  const pending = merged.filter((r) => r.success && !r.command_uuid);
  const failed = merged.filter((r) => !r.success);
  return { merged, applied, pending, failed };
}

/**
 * Show one snackbar with applied / pending / failed counts (no per-device list).
 */
export function formatConfigPushSummaryMessage({ applied, pending, failed }) {
  const appliedN = applied?.length || 0;
  const pendingN = pending?.length || 0;
  const failedN = failed?.length || 0;
  const parts = [];

  if (appliedN > 0) {
    parts.push(`${appliedN} applied`);
  }
  if (pendingN > 0) {
    parts.push(`${pendingN} pending (offline — applies when online)`);
  }
  if (failedN > 0) {
    parts.push(`${failedN} failed`);
  }

  if (parts.length === 0) {
    return { text: 'Configuration saved.', variant: 'success' };
  }

  if (failedN > 0 && appliedN === 0 && pendingN === 0) {
    const hint = failed[0]?.message ? ` ${failed[0].message}` : '';
    return {
      text: `Configuration update failed for ${failedN} device${failedN === 1 ? '' : 's'}.${hint}`,
      variant: 'error',
    };
  }

  return {
    text: `Configuration saved: ${parts.join(', ')}.`,
    variant: failedN > 0 ? 'warning' : (pendingN > 0 && appliedN === 0 ? 'info' : 'success'),
  };
}
